import { AlertSeverity, Box, Button, Typography } from "@outschool/backpack";
import {
  MINIMUM_PRICE_CENTS,
  centsToDollars,
  parseDollarStringToCents
} from "@outschool/business-rules";
import {
  TFunction,
  Trans,
  renderCurrencyString,
  useTranslation
} from "@outschool/localization";
import {
  Input,
  ModalActionButtons,
  Space
} from "@outschool/ui-legacy-component-library";
import { required, useFormState } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import ErrorMessage from "../ErrorMessage";

export const repriceNoticeProps = ({
  t,
  newPrice,
  oldPrice,
  confirmed = false,
  meetingsCanceled = 0
}: {
  t: TFunction;
  newPrice: number;
  oldPrice: number;
  confirmed?: boolean;
  meetingsCanceled?: number;
}) => {
  if (newPrice < oldPrice) {
    return {
      title: (
        <>
          {t("The price is updated from ${{previousPrice}} to ${{newPrice}}", {
            previousPrice: centsToDollars(oldPrice),
            newPrice: centsToDollars(newPrice)
          })}
        </>
      ),
      description: (
        <ul>
          <li>
            {t(
              "Existing paid meetings will not be affected and will maintain their current pricing."
            )}
          </li>
          <li>
            {t(
              "Any upcoming meetings that have not yet been paid for will be charged the updated price."
            )}
          </li>
        </ul>
      )
    };
  } else if (confirmed) {
    return {
      title: (
        <>
          {t(
            "The price is updated from ${{previousPrice}} to ${{newPrice}}, and {{meetingsCanceled}} upcoming meetings are cancelled.",
            {
              previousPrice: centsToDollars(oldPrice),
              newPrice: centsToDollars(newPrice),
              meetingsCanceled
            }
          )}
        </>
      ),
      description: <></>
    };
  } else {
    return {
      severity: "error" as AlertSeverity,
      title: (
        <>
          {t(
            "The price will be raised from ${{previousPrice}} to ${{newPrice}}",
            {
              previousPrice: centsToDollars(oldPrice),
              newPrice: centsToDollars(newPrice)
            }
          )}
        </>
      ),
      description: (
        <>
          <Trans t={t}>
            This action will <b>CANCEL</b> all upcoming unpaid meetings. It is
            important that you communicate these changes with the family before
            proceeding with this action.
          </Trans>
        </>
      )
    };
  }
};

export const EnrollmentPriceChangeConfirmation = ({
  notice,
  onCancel,
  onPriceUpdate
}: {
  onPriceUpdate: () => void;
  notice: React.JSX.Element | null;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );

  return (
    <>
      <Typography variant="h3">{t("Confirm Price Update")}</Typography>
      <hr
        style={{
          marginTop: "1em",
          marginBottom: "1em"
        }}
      />
      {notice}
      <Typography sx={{ marginBottom: "0.5em" }}>
        <Trans t={t}>
          If this learner previously had a recurring lesson, please{" "}
          <b>reschedule the recurring time</b> so that the family can confirm
          the meetings and agree to the new pricing.
        </Trans>
      </Typography>

      <Typography sx={{ display: "block", marginTop: "1em" }}>
        <b>{t("Please confirm that you understand the following:")}</b>
        <ul style={{ paddingLeft: "1em" }}>
          <li>
            <Trans t={t}>
              Any upcoming meetings that have not been paid for will be{" "}
              <b>cancelled</b>.
            </Trans>
          </li>
          <li>
            {t(
              "Existing paid meetings will not be affected and will maintain their current pricing."
            )}
          </li>
          <li>
            {t(
              "The updated pricing will apply to all future meetings that are added."
            )}
          </li>
        </ul>
      </Typography>

      <ModalActionButtons
        primaryLabel={t`Confirm the Price Update`}
        primaryVariant="danger"
        cancelLabel={t`Back to Edit`}
        onCancel={onCancel}
        onPrimary={onPriceUpdate}
        sx={{ marginTop: "1em" }}
      />
    </>
  );
};

export const OneOnOneEnrollmentReprice = ({
  activityPrice,
  enrollmentPrice,
  onPriceUpdate,
  notice
}: {
  activityPrice: number;
  enrollmentPrice: number;
  onPriceUpdate: ({
    price,
    confirmed
  }: {
    price: number;
    confirmed?: boolean;
  }) => void;
  notice: React.JSX.Element | null;
}) => {
  const minPrice = centsToDollars(MINIMUM_PRICE_CENTS);
  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );

  const validatePriceField = (priceCents: number) => {
    const minPriceCents = minPrice * 100;
    if (priceCents < minPriceCents) {
      return t("Class price must be at least {{minimumPriceCents}}", {
        minimumPriceCents: renderCurrencyString({
          priceInCents: MINIMUM_PRICE_CENTS
        })
      });
    }

    return;
  };

  const { fields, errors } = useFormState({
    price: {
      value: centsToDollars(enrollmentPrice).toString(),
      validators: [
        required(() => t("Price is required")),
        price => validatePriceField(parseDollarStringToCents(price)) as string
      ],
      validateOnChange: true
    }
  });

  const hasErrors = !_.isEmpty(errors.price);
  const isSamePrice =
    parseDollarStringToCents(fields.price.value!) === enrollmentPrice;

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        marginBottom: "0.5em"
      }}
    >
      {notice}
      {hasErrors && <ErrorMessage value={errors.price} />}
      <Typography
        variant="h5"
        sx={{
          marginBottom: "0.5em"
        }}
      >
        {t("Price")}
      </Typography>
      <Typography
        sx={{
          marginBottom: "0.3em"
        }}
      >
        {t("Default price for this class is : {{defaultPrice}} per session", {
          defaultPrice: renderCurrencyString({
            priceInCents: activityPrice,
            useFree: true
          })
        })}
      </Typography>
      <Box flex sx={{ alignItems: "center", marginBottom: "0.3em" }}>
        <Typography
          sx={{
            marginRight: "1em",
            fontWeight: "fontWeightBold"
          }}
        >
          {t("Current Price")}
        </Typography>
        $
        <Space x="small" />
        <Input
          data-test-id="price-change-input"
          type="number"
          min={minPrice}
          sx={{ width: "6em" }}
          {...fields.price}
        />
        <Space x="small" />
        {t("USD per session")}
      </Box>
      <Button
        data-test-id="price-change-submit"
        disabled={hasErrors || isSamePrice}
        onClick={() => {
          onPriceUpdate({
            price: parseDollarStringToCents(fields.price.value!)
          });
        }}
        sx={{
          width: "fit-content"
        }}
      >
        {t("Update")}
      </Button>
    </Box>
  );
};
